import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@packages/api';

export function useSaveSummary(callId: number, meetingId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ analysisId, summary }: { analysisId: string; summary: string }) => {
      const res = await apiFetch(`/api/v17/transcribe/analyses/${analysisId}`, {
        method: 'PUT',
        body: JSON.stringify({ summary }),
      });
      if (!res.ok) throw new Error(`Failed to save summary: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transcriptSession', callId, meetingId] });
    },
  });
}

export function useAddTopic(callId: number, meetingId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ analysisId, topic }: { analysisId: string; topic: string }) => {
      const res = await apiFetch(`/api/v17/transcribe/analyses/${analysisId}/topics`, {
        method: 'POST',
        body: JSON.stringify({ text: topic }),
      });
      if (!res.ok) throw new Error(`Failed to add topic: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transcriptSession', callId, meetingId] });
    },
  });
}

export function useDeleteTopic(callId: number, meetingId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ analysisId, topicId }: { analysisId: string; topicId: string }) => {
      const res = await apiFetch(`/api/v17/transcribe/analyses/${analysisId}/topics/${topicId}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error(`Failed to delete topic: ${res.status}`);
      return res.text();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transcriptSession', callId, meetingId] });
    },
  });
}

export function useSaveCallContent(callId: number, contentId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (content: Record<string, unknown>) => {
      const isCreate = !contentId;
      const url = isCreate
        ? `/api/v17/calls/${callId}/content`
        : `/api/v17/calls/${callId}/content/${contentId}`;
      const res = await apiFetch(url, {
        method: isCreate ? 'POST' : 'PUT',
        body: JSON.stringify(isCreate ? content : { id: contentId, ...content }),
      });
      if (!res.ok) throw new Error(`Failed to save call content: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callContent', callId, contentId] });
      queryClient.invalidateQueries({ queryKey: ['call', callId] });
    },
  });
}

export function useDeleteResource(callId: number, contentId: string | undefined, domainId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ resourceId, fileId }: { resourceId: number; fileId: string }) => {
      if (!contentId) throw new Error('No content ID available');

      const contentRes = await apiFetch(
        `/api/v17/calls/${callId}/content/${contentId}/resources/${resourceId}`,
        { method: 'DELETE' },
      );
      if (!contentRes.ok) throw new Error(`Failed to delete resource: ${contentRes.status}`);

      try {
        const storageRes = await apiFetch(
          `/storage/v1/domains/${domainId}/files/${fileId}`,
          { method: 'DELETE' },
        );
        if (!storageRes.ok) {
          console.error(`Failed to delete file ${fileId} from storage: ${storageRes.status}`);
        }
      } catch (err) {
        console.error(`Failed to delete file ${fileId} from storage:`, err);
      }

      return { fileId };
    },
    onSuccess: (_data, { fileId }) => {
      queryClient.invalidateQueries({ queryKey: ['callContent', callId, contentId] });
      queryClient.invalidateQueries({ queryKey: ['call', callId] });
      queryClient.removeQueries({ queryKey: ['resourceThumbnail', domainId, fileId] });
      queryClient.removeQueries({ queryKey: ['resourceFullSize', domainId, fileId] });
      queryClient.removeQueries({ queryKey: ['videoStreamUrl', domainId, fileId] });
    },
  });
}

export function useSaveTranscriptSegment(callId: number, meetingId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ transcriptId, segmentId, text }: { transcriptId: string; segmentId: string; text: string }) => {
      const res = await apiFetch(`/api/v17/transcribe/transcripts/${transcriptId}/segment/${segmentId}`, {
        method: 'PUT',
        body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error(`Failed to save transcript segment: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transcriptSession', callId, meetingId] });
    },
  });
}
